import { GaugeIcon, SparkleIcon } from "@phosphor-icons/react";
import { parseISO } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";
import Buzzsumo from "@/assets/svg/Buzzsumo.svg?react";
import Moz from "@/assets/svg/Moz.svg?react";
import Readership from "@/assets/svg/Readership.svg?react";
import { getEnumLabel } from "@/components/news-feed/utils/filter-lookups";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type { ChartColorInput } from "@/lib/utils/chartUtils";
import { abbreviateNumber } from "@/lib/utils/prettyName";
import type useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import type {
  ChartStackedByOption,
  FormatterOptions,
  Period,
} from "./NewsFeedVolumeGraph.types";

/**
 * Union type representing stack-by values in either old string format or new enum format.
 * This allows functions to accept both formats for backward compatibility.
 */
export type StackByValue = string | ShareVoiceSidebarCategory | null;

export const getChartStackedByOptions = (): ChartStackedByOption[] => [
  {
    label: "All categories",
    value: null,
  },
  {
    label: "LLM Impact Score",
    value: "impact_score",
    icon: <GaugeIcon className={"fill-violet-800"} />,
  },
  {
    label: "Prominence",
    value: "prominence",
  },
  {
    label: "Sentiment",
    value: "sentiment",
    icon: <SparkleIcon className={"fill-violet-800"} />,
  },
  {
    label: "Publication Tier",
    value: "tier",
  },
  {
    label: "Social",
    value: "social",
    icon: <Buzzsumo height={12} />,
  },
  {
    label: "Domain Authority",
    value: "domain_authority",
    icon: <Moz width={20} height={12} />,
  },
  {
    label: "Readership",
    value: "readership",
    icon: <Readership width={10} height={10} />,
  },
  {
    label: "Content Category",
    value: "content_category",
  },
];

export const getFormatterForPeriod = (
  period: Period,
  { startDate, endDate }: FormatterOptions,
): string => {
  const normalizeDate = (
    date: string | number | undefined,
  ): string | undefined => {
    if (typeof date === "number") {
      return new Date(date).toISOString();
    }
    return date;
  };

  // Normalize startDate, endDate
  const normalizedStartDate = normalizeDate(startDate);
  const normalizedEndDate = normalizeDate(endDate);

  if (period && normalizedStartDate && normalizedEndDate) {
    const parseDate = (date: string) => toZonedTime(parseISO(date), "UTC");
    if (period === "day") {
      return format(parseDate(normalizedStartDate), "MMM dd, yyyy");
    }

    if (period === "week") {
      const start = format(parseDate(normalizedStartDate), "MMM d");
      const end = format(parseDate(normalizedEndDate), "d, yyyy");
      return `${start}-${end}`;
    }

    if (period === "month") {
      return format(parseDate(normalizedStartDate), "MMMM");
    }
  }

  return "";
};

const colorKeysByStackedBy = {
  impact_score: [
    {
      active: "violet",
      inactive: "chart-inactive-amethyst-100",
    },
    {
      active: "chart-amethyst-100",
      inactive: "chart-inactive-amethyst-200",
    },
    {
      active: "chart-amethyst-200",
      inactive: "chart-inactive-amethyst-300",
    },
    {
      active: "chart-amethyst-300",
      inactive: "chart-inactive-amethyst-400",
    },
    {
      active: "chart-amethyst-400",
      inactive: "chart-inactive-amethyst-500",
    },
    {
      active: "chart-crimson-100",
      inactive: "chart-inactive-amethyst-600",
    },
    {
      active: "chart-crimson-150",
      inactive: "chart-inactive-crimson-100",
    },
    {
      active: "chart-crimson-200",
      inactive: "chart-inactive-crimson-100",
    },
    {
      active: "chart-crimson-300",
      inactive: "chart-inactive-crimson-100",
    },
    {
      active: "chart-crimson-400",
      inactive: "chart-inactive-crimson-100",
    },
  ],
  sentiment: [
    {
      active: "chart-sage-100",
      inactive: "chart-inactive-sage-100",
    },
    {
      active: "chart-graphite-100",
      inactive: "chart-inactive-graphite-100",
    },
    {
      active: "chart-crimson-500",
      inactive: "chart-inactive-crimson-400",
    },
  ],
  social: [
    {
      active: "chart-ocean-100",
      inactive: "chart-inactive-ocean-100",
    },
    {
      active: "chart-ocean-200",
      inactive: "chart-inactive-ocean-200",
    },
    {
      active: "chart-ocean-250",
      inactive: "chart-inactive-ocean-250",
    },
    {
      active: "chart-ocean-300",
      inactive: "chart-inactive-ocean-300",
    },
    {
      active: "chart-ocean-400",
      inactive: "chart-inactive-ocean-400",
    },
    {
      active: "chart-ocean-500",
      inactive: "chart-inactive-ocean-500",
    },
    {
      active: "chart-ocean-600",
      inactive: "chart-inactive-ocean-600",
    },
    {
      active: "chart-ocean-700",
      inactive: "chart-inactive-ocean-700",
    },
    {
      active: "chart-ocean-800",
      inactive: "chart-inactive-ocean-800",
    },
    {
      active: "chart-ocean-900",
      inactive: "chart-inactive-ocean-900",
    },
    {
      active: "chart-ocean-950",
      inactive: "chart-inactive-ocean-950",
    },
  ],
  tier: [
    {
      active: "chart-ocean-200",
      inactive: "chart-inactive-ocean-200",
    },
    {
      active: "chart-ocean-300",
      inactive: "chart-inactive-ocean-400",
    },
    {
      active: "chart-ocean-500",
      inactive: "chart-inactive-ocean-600",
    },
  ],
  prominence: [
    {
      active: "chart-ocean-250",
      inactive: "chart-inactive-ocean-250",
    },
    {
      active: "chart-ocean-300",
      inactive: "chart-inactive-ocean-300",
    },
    {
      active: "chart-ocean-400",
      inactive: "chart-inactive-ocean-400",
    },
    {
      active: "chart-ocean-500",
      inactive: "chart-inactive-ocean-500",
    },
  ],
};

/**
 * Categories that use ocean color scheme (social colors).
 * These categories share the same color palette.
 */
const OCEAN_COLOR_CATEGORIES = [
  "readership",
  "domain_authority",
  "social",
  "content_category",
] as const;

type OceanColorCategory = (typeof OCEAN_COLOR_CATEGORIES)[number];

/**
 * Gets color configuration for a given stack-by value.
 * Supports both old string format and new enum format.
 *
 * @param stackBy - StackByValue in either old string or new enum format
 * @returns Array of ChartColorInput values for the stack-by category
 *
 * @example
 * ```typescript
 * getColorsForStackBy("sentiment") // Returns sentiment colors
 * getColorsForStackBy("SENTIMENT") // Returns sentiment colors (same result)
 * ```
 */
export function getColorsForStackBy(
  stackBy: StackByValue,
): ChartColorInput[] | undefined {
  if (!stackBy) return ["violet"] as ChartColorInput[];

  // Normalize to old format for color lookup (colors are keyed by old format)
  const normalizedStackBy = normalizeStackByToOldFormat(stackBy);
  if (!normalizedStackBy) return ["violet"] as ChartColorInput[];

  if (
    OCEAN_COLOR_CATEGORIES.includes(normalizedStackBy as OceanColorCategory)
  ) {
    return colorKeysByStackedBy.social as ChartColorInput[];
  }

  const isValidStackBy = (
    stackBy: string,
  ): stackBy is keyof typeof colorKeysByStackedBy =>
    Object.hasOwn(colorKeysByStackedBy, stackBy);

  if (isValidStackBy(normalizedStackBy)) {
    return colorKeysByStackedBy[normalizedStackBy] as ChartColorInput[];
  }

  return ["violet"] as ChartColorInput[];
}

export type StackByKey = string | null;

//TODO move to lib/utils
export const parseToolTipCategory = (category: string): string => {
  if (!category) return category;

  const clean = category.replace(/,/g, "");

  // Handle "X to Y" format
  if (clean.includes(" to ")) {
    const [start, end] = clean.split(" to ").map(Number);
    return `${abbreviateNumber(start || 0)} to ${abbreviateNumber(end || 0)}`;
  }

  // Handle "X-Y" format
  if (clean.includes("-")) {
    const [start, end] = clean.split("-").map(Number);
    return `${abbreviateNumber(start || 0)} - ${abbreviateNumber(end || 0)}`;
  }

  // Handle "X+" format
  if (clean.endsWith("+")) {
    const [number] = [clean.slice(0, -1)].map(Number);
    return `${abbreviateNumber(number || 0)}+`;
  }

  return category;
};

/**
 * Calculates coverage percentage for stacked charts.
 * Supports both old string format and new enum format.
 *
 * @param stackedBy - StackByValue in either old string or new enum format
 * @param filteredCount - Total filtered article count
 * @param totalArticles - Total articles in the stack segment
 * @param volume - Total volume for the period
 * @returns Coverage percentage (0-100)
 */
export const calculateCoveragePercentage = (
  stackedBy: StackByValue,
  filteredCount: number,
  totalArticles: number,
  volume: number,
) => {
  if (stackedBy) {
    return Math.round((totalArticles / volume) * 100);
  }
  return Math.round((volume / filteredCount) * 100);
};

// ============================================================================
// Filter Dictionary Utilities / New Chart DTO
// ============================================================================

/**
 * Mapping from old string format to ShareVoiceSidebarCategory enum.
 * This is the single source of truth for supported mappings.
 */
const OLD_TO_NEW_MAPPING: Record<string, ShareVoiceSidebarCategory> = {
  sentiment: "SENTIMENT",
  tier: "TIER",
  prominence: "PROMINENCE",
  impact_score: "SCORE_BUCKETS",
  social: "SOCIAL_BUCKETS",
  readership: "READERSHIP_BUCKETS",
  domain_authority: "DOMAIN_AUTHORITY_BUCKETS",
  content_category: "CATEGORY",
} as const;

/**
 * Mapping from ShareVoiceSidebarCategory enum to old string format.
 * Derived from OLD_TO_NEW_MAPPING for consistency.
 */
const NEW_TO_OLD_MAPPING: Partial<Record<ShareVoiceSidebarCategory, string>> = {
  SENTIMENT: "sentiment",
  TIER: "tier",
  PROMINENCE: "prominence",
  SCORE_BUCKETS: "impact_score",
  SOCIAL_BUCKETS: "social",
  READERSHIP_BUCKETS: "readership",
  DOMAIN_AUTHORITY_BUCKETS: "domain_authority",
  CATEGORY: "content_category",
  NARRATIVE_CLUSTER: "narrative_clusters",
} as const;

/**
 * Set of valid enum values we support (for fast lookup).
 * Derived from NEW_TO_OLD_MAPPING keys.
 */
const VALID_ENUM_VALUES = new Set(
  Object.keys(NEW_TO_OLD_MAPPING) as ShareVoiceSidebarCategory[],
);

/**
 * Maps stackFeedVolumeBy value to ShareVoiceSidebarCategory enum.
 * Supports both old string format and new enum format.
 * Returns null if the category is not enum-based (e.g., narrative_clusters).
 *
 * @param stackFeedVolumeBy - StackByValue in either old string or new enum format
 * @returns ShareVoiceSidebarCategory enum value or null if not enum-based
 *
 * @example
 * ```typescript
 * getCategoryForStackBy("sentiment") // Returns "SENTIMENT"
 * getCategoryForStackBy("SENTIMENT") // Returns "SENTIMENT" (already enum)
 * getCategoryForStackBy("narrative_clusters") // Returns null
 * ```
 */
export const getCategoryForStackBy = (
  stackFeedVolumeBy: StackByValue,
): ShareVoiceSidebarCategory | null => {
  if (!stackFeedVolumeBy || typeof stackFeedVolumeBy !== "string") {
    return null;
  }

  // First check if it's already a valid enum value (authoritative validation)
  if (VALID_ENUM_VALUES.has(stackFeedVolumeBy as ShareVoiceSidebarCategory)) {
    return stackFeedVolumeBy as ShareVoiceSidebarCategory;
  }

  // Try to map from old format to enum
  return OLD_TO_NEW_MAPPING[stackFeedVolumeBy] ?? null;
};

/**
 * Maps ShareVoiceSidebarCategory enum to old string format for backward compatibility.
 * Returns null if the enum doesn't have a corresponding old string value.
 *
 * @param category - The ShareVoiceSidebarCategory enum value (e.g., "SENTIMENT", "TIER")
 * @returns Old string format (e.g., "sentiment", "tier") or null if not mappable
 *
 * @example
 * ```typescript
 * getStackByFromCategory("SENTIMENT") // Returns "sentiment"
 * getStackByFromCategory("SCORE_BUCKETS") // Returns "impact_score"
 * ```
 */
export const getStackByFromCategory = (
  category: ShareVoiceSidebarCategory | null,
): string | null => {
  if (!category) return null;
  return NEW_TO_OLD_MAPPING[category] ?? null;
};

/**
 * Normalizes a StackByValue to old string format for internal use.
 * If input is already a string, returns as-is. If it's an enum, converts to string.
 *
 * @param value - StackByValue in either old string or new enum format
 * @returns Normalized string value or null
 *
 * @example
 * ```typescript
 * normalizeStackByToOldFormat("sentiment") // Returns "sentiment"
 * normalizeStackByToOldFormat("SENTIMENT") // Returns "sentiment"
 * ```
 */
export const normalizeStackByToOldFormat = (
  value: StackByValue,
): string | null => {
  if (!value || typeof value !== "string") return null;

  // Check if it's already an old format string (exists in mapping)
  if (value in OLD_TO_NEW_MAPPING) {
    return value;
  }

  // If it's an enum value, convert to old format
  return getStackByFromCategory(value as ShareVoiceSidebarCategory);
};

/**
 * Normalizes a StackByValue to new enum format for GraphQL queries.
 * If input is already an enum, returns as-is. If it's a string, converts to enum.
 *
 * @param value - StackByValue in either old string or new enum format
 * @returns Normalized ShareVoiceSidebarCategory enum value or null
 *
 * @example
 * ```typescript
 * normalizeStackByToEnum("sentiment") // Returns "SENTIMENT"
 * normalizeStackByToEnum("SENTIMENT") // Returns "SENTIMENT"
 * ```
 */
export const normalizeStackByToEnum = (
  value: StackByValue,
): ShareVoiceSidebarCategory | null => {
  // getCategoryForStackBy already handles both formats
  return getCategoryForStackBy(value);
};

/**
 * Extracts all display labels from filter dictionary for a given enum-based category.
 * Supports both old string format and new enum format.
 * Returns empty array if category is not enum-based or not found in dictionary.
 *
 * @param stackFeedVolumeBy - StackByValue in either old string or new enum format
 * @param filterDict - The filter dictionary state from useFilterDictionary.getState()
 * @returns Array of display labels (e.g., ["Negative", "Neutral", "Positive"])
 *
 * @example
 * ```typescript
 * const filterDict = useFilterDictionary.getState();
 * const labels = getStackByLabelsFromDictionary("sentiment", filterDict);
 * // Returns: ["Negative", "Neutral", "Positive"]
 * const labels2 = getStackByLabelsFromDictionary("SENTIMENT", filterDict);
 * // Returns: ["Negative", "Neutral", "Positive"] (same result)
 * ```
 */
export const getStackByLabelsFromDictionary = (
  stackFeedVolumeBy: StackByValue,
  filterDict: {
    getDictionaryForCategory: (
      category: ShareVoiceSidebarCategory,
    ) => Array<{ categoryLabel: string; label: string }> | undefined;
  },
): string[] => {
  if (!stackFeedVolumeBy) return [];

  // Normalize to enum format for dictionary lookup
  const category = normalizeStackByToEnum(stackFeedVolumeBy);
  if (!category) return [];

  const items = filterDict.getDictionaryForCategory(category);
  if (!items) return [];

  return items.map((item) => item.label);
};

/**
 * Transforms backend `chartData` JSON to Recharts format, mapping enum/ID keys to display labels
 * and adding `_keyMap` for bar selection.
 * Supports both old string format and new enum format for stackFeedVolumeBy.
 *
 * @param data - Array of volume data periods with chartData field
 * @param stackFeedVolumeBy - StackByValue in either old string or new enum format (e.g., "sentiment", "SENTIMENT", "narrative_clusters")
 * @param filterDict - The filter dictionary state from useFilterDictionary.getState()
 * @param idToLabelMap - Optional map for ID-based categories (narrative clusters) - maps ID to label
 * @returns Transformed data array ready for Recharts with _keyMap for bar selection
 *
 * @example
 * ```typescript
 * // Input (backend DTO)
 * {
 *   startOfPeriod: "2024-01-01T00:00:00Z",
 *   endOfPeriod: "2024-01-02T00:00:00Z",
 *   volume: 100,
 *   chartData: { "NEGATIVE": 30, "POSITIVE": 70 }
 * }
 *
 * // Output (Recharts format)
 * {
 *   startOfPeriod: "2024-01-01T00:00:00Z",
 *   endOfPeriod: "2024-01-02T00:00:00Z",
 *   volume: 100,
 *   "Negative": 30,
 *   "Positive": 70,
 *   _keyMap: { "Negative": "NEGATIVE", "Positive": "POSITIVE" }
 * }
 * ```
 */
export const transformFeedVolumeDataWithEnums = (
  data: Array<{
    startOfPeriod: string;
    endOfPeriod: string;
    volume: number;
    chartData: Record<string, number>;
  }>,
  stackFeedVolumeBy: StackByValue,
  filterDict?: ReturnType<typeof useFilterDictionary.getState>,
  idToLabelMap?: Map<string, string>,
): Array<Record<string, string | number | Record<string, string>>> => {
  return data.map((item) => {
    const { startOfPeriod, endOfPeriod, volume, chartData } = item;

    const transformed: Record<
      string,
      string | number | Record<string, string>
    > = {
      startOfPeriod,
      endOfPeriod,
      volume,
      _keyMap: {},
    };

    // When stackFeedVolumeBy is null (All Categories), only use volume
    // Do not process chartData to avoid creating unwanted categories
    if (!stackFeedVolumeBy) {
      // Return only volume - chart expects categories=["volume"] when stackFeedVolumeBy is null
      return transformed;
    }

    // Check for narrative clusters (ID-based, not enum-based)
    // Handle both old format "narrative_clusters" and enum format "NARRATIVE_CLUSTER"
    const isNarrativeClusters =
      stackFeedVolumeBy === "narrative_clusters" ||
      stackFeedVolumeBy === "NARRATIVE_CLUSTER";

    // Map chartData keys to display labels
    for (const originalKey of Object.keys(chartData)) {
      const value = chartData[originalKey];
      if (value === undefined) continue;

      let displayLabel = originalKey;

      if (isNarrativeClusters && idToLabelMap) {
        // Lookup label by ID for narrative clusters
        displayLabel = idToLabelMap.get(originalKey) ?? originalKey;
      } else {
        // Map enum to display label using filter dictionary
        const category = normalizeStackByToEnum(stackFeedVolumeBy);
        if (category) {
          // getEnumLabel will use filterDict if provided, otherwise fallback to store or prettified label
          displayLabel = getEnumLabel(category, originalKey, filterDict);
        }
      }

      // Store value with display label as key (for Recharts)
      transformed[displayLabel] = value;

      // Store original key mapping (for bar selection)
      const keyMap = transformed._keyMap as Record<string, string>;
      keyMap[displayLabel] = originalKey;
    }

    return transformed;
  });
};
